import { ColumnType } from '@ecosystem/export-api';

var Denominator;
(function (Denominator) {
    Denominator["None"] = "";
    Denominator["Thousands"] = "k";
    Denominator["Millions"] = "m";
    Denominator["Billions"] = "b";
    Denominator["Percentage"] = "%";
})(Denominator || (Denominator = {}));
const getSuffix = (den) => {
    return den.valueOf();
};
const getValue = (den) => {
    switch (den) {
        case Denominator.Thousands:
            return 1000;
        case Denominator.Millions:
            return 1000000;
        case Denominator.Billions:
            return 1000000000;
        case Denominator.None:
        default:
            return 1;
    }
};

const DEFAULT_CURRENCY = '';
const DEFAULT_DENOMINATOR = Denominator.None;
const DEFAULT_DECIMALS = 0;
const DEFAULT_PERCENTAGE = false;
const DEFAULT_PERCENTAGE_SYMBOL = '%';
const DEFAULT_GROWTH = false;
const LARGE_POSITIVE = '>';
const LARGE_NEGATIVE = '<';
const DEFAULT_NO_POSTFIX = false;
const SCIENTIFIC_FORMAT_THRESHOLD = 100000000000;
const SCIENTIFIC_DECIMALS = 2;
const DEFAULT_CONFIG = {
    currency: DEFAULT_CURRENCY,
    denominator: DEFAULT_DENOMINATOR,
    percentageSymbol: DEFAULT_PERCENTAGE_SYMBOL,
    percentage: DEFAULT_PERCENTAGE,
    growth: DEFAULT_GROWTH,
    decimals: DEFAULT_DECIMALS,
    noPostfix: DEFAULT_NO_POSTFIX,
};
function addTrailingZeroes(valueString, decimals) {
    if (valueString.includes('.')) {
        const components = valueString.split('.');
        if (components.length > 2) {
            throw new Error('addTrailingZeroes: passed a string with more than one decimal point.');
        }
        const integerComponent = components[0];
        const decimalComponent = components[1];
        const decimalThings = [...Array(decimals - decimalComponent.length)]
            .map(() => '0')
            .join('');
        return `${integerComponent}.${decimalComponent}${decimalThings}`;
    }
    return `${valueString}.${[...Array(decimals)].map(() => '0').join('')}`;
}
function formatAmount(value, config) {
    const formattedNumber = formatNumber(value, config);
    if (config.replaceZeroValueWith && formattedNumber === 0) {
        return `${config.replaceZeroValueWith}`;
    }
    const prefixValue = prefix(value, config);
    const postfixValue = postfix(config);
    const decimals = config.decimals === undefined ? 2 : config.decimals;
    if (formattedNumber >= SCIENTIFIC_FORMAT_THRESHOLD) {
        return `${prefixValue}${scientificExpression(formattedNumber)}`;
    }
    let valueString = localeString(formattedNumber, decimals);
    if (needsTrailingZeroes(formattedNumber, decimals)) {
        valueString = `${addTrailingZeroes(valueString, decimals)}`;
    }
    const isNegativeCurrency = config.currency && valueString.startsWith('-');
    return `${isNegativeCurrency ? '-' : ''}${prefixValue}${isNegativeCurrency
        ? valueString.substring(1, valueString.length)
        : valueString}${postfixValue}`;
}
function localeString(input, decimals) {
    return input.toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}
function needsTrailingZeroes(input, decimals) {
    if (Number.isInteger(input)) {
        return decimals > 0;
    }
    const stringInput = input.toString();
    return stringInput.split('.')[1].length < decimals;
}
function autoFormatAmount(value, config) {
    const autoConfig = autoGenerateFormatConfig(config);
    if (config &&
        (config.denominator || config.denominator === '' || config.percentage)) {
        return formatAmount(value, autoConfig);
    }
    if (config && config.currency) {
        const denominator = getLargestDenominator(value);
        const decimals = denominator === Denominator.None ? 2 : 1;
        return formatAmount(value, Object.assign(Object.assign({}, autoConfig), { denominator, decimals }));
    }
    if (config && config.dynamicDenominator) {
        const denominator = getLargestDenominator(value);
        const decimals = denominator === Denominator.None ? 0 : 1;
        return formatAmount(value, Object.assign(Object.assign({}, autoConfig), { denominator, decimals }));
    }
    return formatAmount(value, autoConfig);
}
function autoGenerateFormatConfig(config) {
    return Object.assign(Object.assign({}, DEFAULT_CONFIG), config);
}
function roundToDigits(value, radix) {
    const radixValue = radix === undefined ? 2 : radix;
    const factor = Math.pow(10, radixValue);
    return Math.round(value * factor) / factor;
}
function getLargestDenominator(value) {
    const largestExponent = Math.floor(Math.log10(Math.abs(value)));
    if (largestExponent >= 12) {
        return Denominator.None;
    }
    if (largestExponent >= 9) {
        return Denominator.Billions;
    }
    if (largestExponent >= 6) {
        return Denominator.Millions;
    }
    if (largestExponent >= 4) {
        return Denominator.Thousands;
    }
    return Denominator.None;
}
function formatNumber(value, config) {
    if (value < -999 && config.percentage) {
        return -999;
    }
    if (value < 1000) {
        return roundToDigits(value / getValue(config.denominator || Denominator.None), config.decimals);
    }
    if (config.percentage) {
        return 999;
    }
    return roundToDigits(value / getValue(config.denominator || Denominator.None), config.decimals);
}
function scientificExpression(value) {
    return value.toExponential(SCIENTIFIC_DECIMALS);
}
function postfix(config) {
    if (config.noPostfix) {
        return '';
    }
    const postfixConfig = {
        percentage: false,
        percentageSymbol: '%',
        denominator: '',
    };
    postfixConfig.percentage = config.percentage || postfixConfig.percentage;
    postfixConfig.percentageSymbol = config.percentageSymbol
        ? config.percentageSymbol
        : postfixConfig.percentageSymbol;
    postfixConfig.denominator = config.denominator
        ? getSuffix(config.denominator)
        : getSuffix(Denominator.None);
    return `${postfixConfig.denominator}${postfixConfig.percentage ? postfixConfig.percentageSymbol : ''}`;
}
function prefix(value, config) {
    const prefixConfig = {
        largeNumber: '',
        growth: '',
        currency: '',
    };
    if (config.percentage) {
        prefixConfig.largeNumber =
            value > 999 ? LARGE_POSITIVE : prefixConfig.largeNumber;
        prefixConfig.largeNumber =
            value < -999 ? LARGE_NEGATIVE : prefixConfig.largeNumber;
    }
    prefixConfig.growth = config.growth && value > 0 ? '+' : prefixConfig.growth;
    prefixConfig.currency = config.currency
        ? config.currency
        : prefixConfig.currency;
    return `${prefixConfig.largeNumber}${prefixConfig.growth}${prefixConfig.currency}`;
}

const Kpi = {
    salesUnits: 'salesUnits',
    salesUnitShare: 'salesUnitShare',
    revenue: 'revenue',
    revenueShare: 'revenueShare',
    averagePrice: 'averagePrice',
    numericDistribution: 'numericDistribution',
    weightedNumericDistribution: 'weightedNumericDistribution',
    priceElasticity: 'priceElasticity',
    tprUnits: 'tprUnits',
    tprPercentage: 'tprPercentage',
    tprEfficiency: 'tprEfficiency',
    tprElasticity: 'tprElasticity',
    salesUnitsUplift: 'salesUnitsUplift',
    tprUpliftUnits: 'tprUpliftUnits',
    weightedCoverage: 'weightedCoverage',
    priceReduction: 'priceReduction',
    premiumRevenue: 'premiumRevenue',
    premiumRevenueShare: 'premiumRevenueShare',
    marketRevenue: 'marketRevenue',
    marketUnits: 'marketUnits',
    brandRevenue: 'brandRevenue',
    brandRevenueShare: 'brandRevenueShare',
    brandUnits: 'brandUnits',
    brandUnitsShare: 'brandUnitsShare',
    price: 'price',
    revenueChange: 'revenueChange',
    salesUnitChange: 'salesUnitChange',
    priceIndex: 'priceIndex',
};
function isKnownKpi(kpi) {
    return Object.values(Kpi).includes(kpi);
}

const NOT_AVAILABLE = '-';
const defaultCurrency = '??';
const kpiDisplayNames = {
    [Kpi.revenue]: 'Revenue',
    [Kpi.revenueShare]: 'Revenue share',
    [Kpi.revenueChange]: 'Revenue change',
    [Kpi.salesUnits]: 'Units',
    [Kpi.salesUnitShare]: 'Unit share',
    [Kpi.salesUnitChange]: 'Unit change',
    [Kpi.averagePrice]: 'Price',
    [Kpi.numericDistribution]: 'Active numeric distribution',
    [Kpi.weightedNumericDistribution]: 'Active weighted distribution',
    [Kpi.priceElasticity]: 'Price elasticity',
    [Kpi.tprElasticity]: 'TPR Elasticity',
    [Kpi.weightedCoverage]: 'Weighted TPR coverage',
    [Kpi.tprUnits]: 'TPR units',
    [Kpi.tprPercentage]: 'TPR intensity',
    [Kpi.priceReduction]: 'Price reduction',
    [Kpi.tprUpliftUnits]: 'TPR units uplift',
    [Kpi.tprEfficiency]: 'TPR efficiency',
    [Kpi.premiumRevenue]: 'Revenue Premium',
    [Kpi.premiumRevenueShare]: 'Revenue Premium impact',
    [Kpi.priceIndex]: 'Price index',
};

var ValueType;
(function (ValueType) {
    ValueType[ValueType["ABSOLUTE"] = 0] = "ABSOLUTE";
    ValueType[ValueType["PERCENTAGE_CHANGE"] = 1] = "PERCENTAGE_CHANGE";
    ValueType[ValueType["ABSOLUTE_CHANGE"] = 2] = "ABSOLUTE_CHANGE";
})(ValueType || (ValueType = {}));
var GrowthTypes;
(function (GrowthTypes) {
    GrowthTypes["actual"] = "actual";
    GrowthTypes["percentage"] = "percentage";
})(GrowthTypes || (GrowthTypes = {}));

const formatKpi = (kpi, value, options = {}) => {
    if (value === null || value === undefined) {
        return NOT_AVAILABLE;
    }
    switch (kpi) {
        case Kpi.averagePrice:
        case Kpi.revenue:
        case Kpi.premiumRevenue:
        case Kpi.brandRevenue:
        case Kpi.price:
        case Kpi.marketRevenue:
            return formatMoneyKpi(value, options);
        case Kpi.salesUnits:
        case Kpi.tprUnits:
        case Kpi.salesUnitsUplift:
        case Kpi.tprUpliftUnits:
        case Kpi.brandUnits:
        case Kpi.marketUnits:
            return formatUnitsKpi(value, options);
        case Kpi.numericDistribution:
        case Kpi.weightedNumericDistribution:
            return formatPercentageKpi(value, Object.assign({ isDecimalPercent: true }, options));
        case Kpi.revenueShare:
        case Kpi.salesUnitShare:
        case Kpi.premiumRevenueShare:
        case Kpi.revenueChange:
        case Kpi.salesUnitChange:
        case Kpi.brandRevenueShare:
        case Kpi.brandUnitsShare:
            return formatPercentageKpi(value, Object.assign(Object.assign({}, options), { isDecimalPercent: false }));
        case Kpi.weightedCoverage:
        case Kpi.priceReduction:
            return formatPercentage(value, 1, options.isDecimalPercent);
        case Kpi.tprPercentage:
            return formatPercentage(value, 0, options.isDecimalPercent);
        case Kpi.priceElasticity:
        case Kpi.tprEfficiency:
        case Kpi.tprElasticity:
            return formatDecimal(value, 1);
        case Kpi.priceIndex:
            return formatIndexKpi(value, options);
        default:
            return 'ERROR';
    }
};
const getFormattedOptions = (options) => {
    var _a;
    return (Object.assign(Object.assign({}, options), { as: (_a = options.as) !== null && _a !== void 0 ? _a : ValueType.ABSOLUTE }));
};
const getFormattedCurrency = (currencySymbol, hideCurrency = false) => {
    const currency = (currencySymbol === null || currencySymbol === void 0 ? void 0 : currencySymbol.length) ? currencySymbol : defaultCurrency;
    return hideCurrency ? '' : currency;
};
const formatPercentageKpi = (value, options) => {
    if (value === null || value === undefined) {
        return '';
    }
    const formattedOptions = Object.assign({}, getFormattedOptions(options));
    if (formattedOptions.as === ValueType.ABSOLUTE_CHANGE) {
        let percentageValue = value;
        if (formattedOptions.isDecimalPercent) {
            percentageValue = value * 100;
        }
        const formatted = formatDecimal(percentageValue, 1);
        return `${getChangedPrefix(value)}${formatted}pp`;
    }
    if (formattedOptions.as === ValueType.PERCENTAGE_CHANGE) {
        const formatted = formatPercentage(value, 1, formattedOptions.isDecimalPercent);
        return `${getChangedPrefix(value)}${formatted}`;
    }
    return formatPercentage(value, 1, formattedOptions.isDecimalPercent);
};
const formatUnitsKpi = (value, options) => {
    const formattedOptions = Object.assign({}, getFormattedOptions(options));
    if (formattedOptions.as === ValueType.ABSOLUTE_CHANGE) {
        const formatted = formatUnits(value);
        return `${getChangedPrefix(value)}${formatted}`;
    }
    if (formattedOptions.as === ValueType.PERCENTAGE_CHANGE) {
        const formatted = formatPercentage(value, 1);
        return `${getChangedPrefix(value)}${formatted}`;
    }
    return formatUnits(value);
};
const formatMoneyKpi = (value, options) => {
    const formattedOptions = Object.assign(Object.assign({}, getFormattedOptions(options)), { currencySymbol: getFormattedCurrency(options.currencySymbol, options.hideCurrency) });
    if (formattedOptions.as === ValueType.ABSOLUTE_CHANGE) {
        const formatted = formatCurrency(value, formattedOptions.currencySymbol);
        return `${getChangedPrefix(value)}${formatted}`;
    }
    if (formattedOptions.as === ValueType.PERCENTAGE_CHANGE) {
        const formatted = formatPercentage(value, 1, formattedOptions.isDecimalPercent);
        return `${getChangedPrefix(value)}${formatted}`;
    }
    return formatCurrency(value, formattedOptions.currencySymbol);
};
const getChangedPrefix = (value) => {
    if (value > 0) {
        return '+';
    }
    else {
        return '';
    }
};
const formatPercentage = (value, decimals = 1, isDecimalPercent = false) => {
    if (value === null || value === undefined) {
        return NOT_AVAILABLE;
    }
    const perValue = isDecimalPercent ? value * 100 : value;
    return autoFormatAmount(perValue, {
        decimals,
        percentage: true,
    });
};
const formatCurrency = (value, symbol) => {
    if (value === null || value === undefined) {
        return NOT_AVAILABLE;
    }
    const config = {
        currency: symbol,
        decimals: 2,
    };
    config.denominator = getLargestDenominator(value);
    if (config.denominator !== Denominator.None) {
        config.decimals = 1;
    }
    return autoFormatAmount(value, config);
};
const formatUnits = (value) => {
    if (value === null || value === undefined) {
        return NOT_AVAILABLE;
    }
    const config = {
        decimals: 0,
    };
    config.denominator = getLargestDenominator(value);
    if (config.denominator !== Denominator.None) {
        config.decimals = 1;
    }
    return autoFormatAmount(value, config);
};
const formatDecimal = (value, decimals = 1) => {
    if (value === null || value === undefined) {
        return NOT_AVAILABLE;
    }
    return autoFormatAmount(value, { decimals });
};
const formatIndexKpi = (value, options) => {
    if (value === null || value === undefined) {
        return NOT_AVAILABLE;
    }
    if (ValueType.ABSOLUTE_CHANGE === options.as ||
        ValueType.PERCENTAGE_CHANGE === options.as) {
        return autoFormatAmount(value, { decimals: 0, growth: true });
    }
    return autoFormatAmount(value, { decimals: 0 });
};

const formatGrowth = (kpi, percentOrActualValue, currencySymbol, growth) => {
    if (!growth || growth.available === false) {
        return '-';
    }
    const isActualGrowth = percentOrActualValue === GrowthTypes.actual;
    const growthValue = isActualGrowth ? growth === null || growth === void 0 ? void 0 : growth.change : growth === null || growth === void 0 ? void 0 : growth.changePercent;
    const formatBy = isActualGrowth
        ? ValueType.ABSOLUTE_CHANGE
        : ValueType.PERCENTAGE_CHANGE;
    return formatKpi(kpi, growthValue, { currencySymbol, as: formatBy });
};

class ExcelFormatter {
    formatUnits(value) {
        return value === undefined || value === null
            ? null
            : roundToDigits(value, 0);
    }
    formatDecimalKpi(value, fractionDigits = 2) {
        return value === undefined || value === null
            ? null
            : roundToDigits(value, fractionDigits);
    }
    formatPercentage(value) {
        return value === undefined || value === null
            ? null
            : this.formatDecimalKpi(value / 100, 3);
    }
    formatNumericDistribution(value) {
        return value === undefined || value === null
            ? null
            : this.formatPercentage(value * 100);
    }
    formatNumericDistributionChange(value) {
        return value === undefined || value === null
            ? null
            : this.formatDecimalKpi(value * 100, 1);
    }
    formatShareChange(value) {
        return this.formatDecimalKpi(value, 1);
    }
}

const moneyKpis = new Set([
    Kpi.revenue,
    Kpi.averagePrice,
    Kpi.premiumRevenue,
    Kpi.marketRevenue,
    Kpi.brandRevenue,
]);
const shareKpis = new Set([
    Kpi.premiumRevenueShare,
    Kpi.revenueShare,
    Kpi.salesUnitShare,
    Kpi.brandRevenueShare,
    Kpi.brandUnitsShare,
]);
const distributionKpis = new Set([
    Kpi.numericDistribution,
    Kpi.weightedNumericDistribution,
]);
const unitsKpis = new Set([
    Kpi.salesUnits,
    Kpi.tprUpliftUnits,
    Kpi.tprUnits,
    Kpi.marketUnits,
    Kpi.brandUnits,
]);
const elasticityKpis = new Set([Kpi.priceElasticity, Kpi.tprElasticity]);
function formatKpiForExport(kpi, fact, formatter) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (moneyKpis.has(kpi)) {
        return [
            formatter.formatDecimalKpi(fact === null || fact === void 0 ? void 0 : fact.value, 2),
            formatter.formatPercentage((_a = fact === null || fact === void 0 ? void 0 : fact.growth) === null || _a === void 0 ? void 0 : _a.changePercent),
            formatter.formatDecimalKpi((_b = fact === null || fact === void 0 ? void 0 : fact.growth) === null || _b === void 0 ? void 0 : _b.change, 2),
        ];
    }
    if (unitsKpis.has(kpi)) {
        return [
            formatter.formatUnits(fact === null || fact === void 0 ? void 0 : fact.value),
            formatter.formatPercentage((_c = fact === null || fact === void 0 ? void 0 : fact.growth) === null || _c === void 0 ? void 0 : _c.changePercent),
            formatter.formatUnits((_d = fact === null || fact === void 0 ? void 0 : fact.growth) === null || _d === void 0 ? void 0 : _d.change),
        ];
    }
    if (distributionKpis.has(kpi)) {
        return [
            formatter.formatNumericDistribution(fact === null || fact === void 0 ? void 0 : fact.value),
            formatter.formatNumericDistributionChange((_e = fact === null || fact === void 0 ? void 0 : fact.growth) === null || _e === void 0 ? void 0 : _e.change),
        ];
    }
    if (shareKpis.has(kpi)) {
        return [
            formatter.formatPercentage(fact === null || fact === void 0 ? void 0 : fact.value),
            formatter.formatShareChange((_f = fact === null || fact === void 0 ? void 0 : fact.growth) === null || _f === void 0 ? void 0 : _f.change),
        ];
    }
    if (elasticityKpis.has(kpi)) {
        return [formatter.formatDecimalKpi(fact === null || fact === void 0 ? void 0 : fact.value, 1)];
    }
    if (kpi === Kpi.tprEfficiency) {
        return [formatter.formatDecimalKpi(fact === null || fact === void 0 ? void 0 : fact.value, 1)];
    }
    if (kpi === Kpi.priceReduction) {
        return [formatter.formatNumericDistribution(fact === null || fact === void 0 ? void 0 : fact.value)];
    }
    if (kpi === Kpi.tprPercentage) {
        return [formatter.formatPercentage((fact === null || fact === void 0 ? void 0 : fact.value) ? fact.value * 100 : null)];
    }
    if (kpi === Kpi.priceIndex) {
        return [
            formatter.formatDecimalKpi(fact === null || fact === void 0 ? void 0 : fact.value, 0),
            formatter.formatDecimalKpi((_g = fact === null || fact === void 0 ? void 0 : fact.growth) === null || _g === void 0 ? void 0 : _g.change, 0),
        ];
    }
    return [];
}
function getExportColumns(currency, kpis) {
    return kpis.flatMap((kpi) => {
        const kpiName = kpi.name;
        if (kpiName === undefined) {
            return [];
        }
        const value = kpiName +
            (moneyKpis.has(kpi.type)
                ? ` (${currency})`
                : shareKpis.has(kpi.type)
                    ? ' (%)'
                    : '');
        if (distributionKpis.has(kpi.type) || shareKpis.has(kpi.type)) {
            return [
                { header: value, type: ColumnType.Percentage },
                { header: `${kpiName} Change (pp)`, type: ColumnType.PercentagePoint },
            ];
        }
        if (moneyKpis.has(kpi.type)) {
            return [
                { header: value, type: ColumnType.Currency },
                { header: `${kpiName} Change (%)`, type: ColumnType.Percentage },
                {
                    header: `${kpiName} Change (${currency})`,
                    type: ColumnType.Currency,
                },
            ];
        }
        if (unitsKpis.has(kpi.type)) {
            return [
                { header: value, type: ColumnType.Number },
                { header: `${kpiName} Change (%)`, type: ColumnType.Percentage },
                { header: `${kpiName} Change`, type: ColumnType.Number },
            ];
        }
        if (elasticityKpis.has(kpi.type)) {
            return [{ header: value, type: ColumnType.Number }];
        }
        if (kpi.type === Kpi.tprEfficiency) {
            return [{ header: value, type: ColumnType.Number }];
        }
        if (kpi.type === Kpi.priceReduction || kpi.type === Kpi.tprPercentage) {
            return [{ header: value, type: ColumnType.Percentage }];
        }
        if (kpi.type === Kpi.priceIndex) {
            return [
                { header: value, type: ColumnType.Number },
                { header: `${kpiName} Change`, type: ColumnType.Number },
            ];
        }
        return [];
    });
}

export { DEFAULT_CONFIG, Denominator, ExcelFormatter, GrowthTypes, Kpi, NOT_AVAILABLE, ValueType, addTrailingZeroes, autoFormatAmount, autoGenerateFormatConfig, defaultCurrency, formatAmount, formatCurrency, formatDecimal, formatGrowth, formatIndexKpi, formatKpi, formatKpiForExport, formatMoneyKpi, formatPercentage, formatPercentageKpi, formatUnits, formatUnitsKpi, getExportColumns, getLargestDenominator, getSuffix, getValue, isKnownKpi, kpiDisplayNames, localeString, postfix, prefix, roundToDigits };
//# sourceMappingURL=index.es.js.map
